import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Phone, Mail, MapPin, Send, CheckCircle2 } from "lucide-react";
import LocationMap from "@/components/LocationMap";

export default function ContactPage() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", subject: params.get("vorrat") ? "Anfrage zu Vorratsgesellschaft" : "Erstgespräch", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="pt-32 pb-24 bg-slate-50 min-h-screen" data-testid="contact-page">
      <div className="mbw-container">
        <div className="max-w-3xl mb-12">
          <div className="mbw-overline mb-4">Kontakt</div>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter text-[#1A2433] leading-[1.02] mb-4">
            Reden wir <span className="text-[#991B1B]">miteinander.</span>
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed">
            Schreiben Sie uns – wir melden uns innerhalb eines Werktages.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="space-y-4">
            <div className="bg-[#1A2433] text-white rounded-2xl p-8">
              <h3 className="font-heading text-xl font-semibold mb-6">Direktkontakt</h3>
              <ul className="space-y-5">
                <li className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-[#991B1B] mt-0.5" />
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-400">Telefon</div>
                    <a href="tel:+41525510140" className="font-medium hover:text-[#991B1B]">+41 52 551 01 40</a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-[#991B1B] mt-0.5" />
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-400">E-Mail</div>
                    <a href="mailto:office@mbw-swiss.ch" className="font-medium hover:text-[#991B1B]">office@mbw-swiss.ch</a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#991B1B] mt-0.5" />
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-400">Hauptsitz</div>
                    <div className="font-medium">Wildenstrasse 3<br />8212 Neuhausen am Rheinfall</div>
                    <div className="text-xs text-slate-400 mt-2">Weitere Büros in den Kantonen Schaffhausen und Zürich – Termine vor Ort nach Vereinbarung.</div>
                  </div>
                </li>
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-slate-200">
              <h4 className="font-heading font-semibold text-[#1A2433] mb-2">Schon Kunde?</h4>
              <p className="text-sm text-slate-600 mb-3">Loggen Sie sich in Ihr Portal ein.</p>
              <a href="/login" className="btn-secondary !py-2 !text-sm w-full justify-center">Zum Login</a>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-2 bg-white rounded-2xl p-8 border border-slate-200 space-y-5" data-testid="contact-form">
            {sent ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-[#1A2433] mb-2">Vielen Dank!</h3>
                <p className="text-slate-600">Wir haben Ihre Nachricht erhalten und melden uns in Kürze.</p>
              </div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="contact-name" />
                  <Field label="E-Mail" required type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="contact-email" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Telefon" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="contact-phone" />
                  <Field label="Betreff" required value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} testid="contact-subject" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A2433] mb-2">Nachricht *</label>
                  <textarea
                    required rows={6}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#991B1B]"
                    data-testid="contact-message"
                  />
                </div>
                {error && <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">{error}</div>}
                <label className="flex items-start gap-2 text-sm text-slate-600 cursor-pointer select-none" data-testid="contact-agb-wrapper">
                  <input
                    type="checkbox"
                    required
                    checked={!!form.agb}
                    onChange={(e) => setForm({ ...form, agb: e.target.checked })}
                    className="mt-1 w-4 h-4 accent-[#991B1B]"
                    data-testid="contact-agb-checkbox"
                  />
                  <span>
                    Ich habe die <Link to="/impressum#agb" className="text-[#991B1B] underline hover:no-underline">AGB</Link> und die{" "}
                    <Link to="/datenschutz" className="text-[#991B1B] underline hover:no-underline">Datenschutzerklärung</Link> gelesen und akzeptiere diese.
                  </span>
                </label>
                <button type="submit" disabled={busy || !form.agb} className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed" data-testid="contact-submit">
                  {busy ? "Sende…" : <>Nachricht senden <Send className="w-4 h-4" /></>}
                </button>
              </>
            )}
          </form>
        </div>

        {/* Map */}
        <div className="mt-10" data-testid="contact-map-wrapper">
          <LocationMap />
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#1A2433] mb-2">{label}{required && " *"}</label>
      <input
        type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#991B1B]"
        data-testid={testid}
      />
    </div>
  );
}
